import {
  TeButton,
  TeContainer,
  TeContainerSlim,
  TeDivider,
  TeIcon,
  TeInputText,
  TeModal,
  TePagination,
  TePlaceholderContainer,
  TePopover,
  TeSearch,
  TeStep,
  TeSteps,
  TeText,
  TeTextarea,
} from '@topcon/tuicl-react';
import { Statuses } from '@topcon/tuicl/dist/common/component-types';
import { useNotification } from '@topcon/topcon-utils';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { IdentityBody } from '../../api/generated/pap';
import { PageWrapper } from '../../utils/styling';
import { copyToClipboard } from '../../utils/utils';
import { generateSupportToken } from './topconId.mutations';
import { useIdentities } from './topconId.queries';
import TopconIdUser from './TopconIdUser';

interface StoredIdentity {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
}

const localStorageKey = 'tn-topconid-recently-used';

const getStoredRecents = (): IdentityBody[] => {
  const stored = localStorage.getItem(localStorageKey);
  if (!stored) return [];
  try {
    const parsed: unknown = JSON.parse(stored);
    if (!Array.isArray(parsed)) return [];
    return (parsed as StoredIdentity[]).map((item) => ({
      id: item.id,
      firstName: item.firstName,
      lastName: item.lastName,
      email: item.email,
      archived: false,
      createdAt: new Date().toISOString(),
      modifiedAt: new Date().toISOString(),
      trn: '',
    }));
  } catch {
    return [];
  }
};

const pageLimit = 99;

function TopconId() {
  const { t } = useTranslation('topconId');
  const notificationService = useNotification();
  const [search, setSearch] = useState('');
  const [searchForIdentity, setSearchForIdentity] = useState<string | null>(null);
  const [recentIdentities, setRecentIdentities] = useState<IdentityBody[]>(getStoredRecents());
  const [selectedPage, setSelectedPage] = useState(1);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalStep, setModalStep] = useState<'form' | 'result'>('form');
  const [isGenerating, setIsGenerating] = useState(false);
  const [supportToken, setSupportToken] = useState('');
  const [selectedIdentity, setSelectedIdentity] = useState<IdentityBody | null>(null);
  const [reason, setReason] = useState('');
  const [ticket, setTicket] = useState('');

  const isSearchActive = searchForIdentity !== null;

  const { data: identitiesResponse, isLoading } = useIdentities({
    limit: pageLimit,
    page: selectedPage,
    emailFilter: isSearchActive ? searchForIdentity : undefined,
    isEnabled: isSearchActive,
  });

  const identities = useMemo(() => identitiesResponse?.items ?? [], [identitiesResponse?.items]);

  const totalPages = identitiesResponse?.total
    ? Math.ceil(identitiesResponse.total / pageLimit)
    : 1;

  const runSearch = useCallback(() => {
    setSelectedPage(1);
    if (search.length < 4) setSearchForIdentity(null);
    else setSearchForIdentity(search);
  }, [search]);

  const storeAsRecent = useCallback(
    (identity: IdentityBody) => {
      const filtered = recentIdentities.filter((i) => i.id !== identity.id);
      const newRecent = [identity, ...filtered].slice(0, 3);
      setRecentIdentities(newRecent);

      const storageFormat = newRecent.map((id) => ({
        id: id.id,
        firstName: id.firstName,
        lastName: id.lastName,
        email: id.email,
      }));

      try {
        localStorage.setItem(localStorageKey, JSON.stringify(storageFormat));
      } catch {
        // localStorage may be full — recents won't persist but the UI still works
      }
    },
    [recentIdentities]
  );

  const handleSupport = useCallback(
    async (identity: IdentityBody) => {
      storeAsRecent(identity);
      setSelectedIdentity(identity);
      setReason('');
      setTicket('');
      setSupportToken('');
      setModalStep('form');
      setModalOpen(true);
    },
    [storeAsRecent]
  );

  const handleGenerateToken = useCallback(async () => {
    if (!selectedIdentity || !reason.trim() || !ticket.trim()) return;

    setIsGenerating(true);
    try {
      const token = await generateSupportToken({
        identityId: selectedIdentity.id,
        reason: reason.trim(),
        ticket: ticket.trim(),
      });
      setSupportToken(token);
      setModalStep('result');
    } catch {
      setSupportToken(t('supportTokenError'));
      setModalStep('result');
    } finally {
      setIsGenerating(false);
    }
  }, [selectedIdentity, reason, ticket, t]);

  const handleCopyToken = useCallback(async () => {
    const success = await copyToClipboard(supportToken);
    if (success) {
      notificationService.addNotification(t('tokenCopied'), '', Statuses.success);
    }
  }, [supportToken, notificationService, t]);

  const renderSearch = useCallback(() => {
    return (
      <TeContainerSlim fill gap="xs2">
        <TeSearch
          value={search}
          techange={(e) => setSearch(e.target.value)}
          placeholder={t('searchPlaceholder')}
          clearable
          size="s"
          valid={search.length >= 4 || search.length === 0}
          teblur={runSearch}
          teclear={() => {
            setSelectedPage(1);
            setSearchForIdentity(null);
          }}
          tekeydown={(e) => {
            if (e.key === 'Enter') runSearch();
          }}
          testId="topconid-search-input"
        />
        <TeButton
          variant="neutral"
          icon="arrow_forward"
          size="s"
          disabled={search.length < 4}
          teclick={runSearch}
          testId="topconid-search-button"
        />
      </TeContainerSlim>
    );
  }, [runSearch, search, t]);

  const renderHeader = useCallback(
    () => (
      <TeContainerSlim justifyContent="center" fill>
        <TeContainerSlim column alignItems="center" padding={['xl', 's', 'l', 's']} gap="xl">
          <TeContainerSlim column gap="xs" alignItems="center">
            <TeText size="xl" weight="bold">
              {t('searchForIdentity')}
            </TeText>
            <TeContainerSlim gap="xs" alignItems="center">
              <TeText>{t('searchByEmail')}</TeText>
              <TeIcon name="info" popkey="topconid-info-icon" />
              <TePopover
                variant="secondary"
                placement="bottom"
                for="topconid-info-icon"
                operation="hover"
              >
                <TeContainer width="256">
                  <TeText size="s" color="text.inverse">
                    {t('searchByEmailInfo')}
                  </TeText>
                </TeContainer>
              </TePopover>
            </TeContainerSlim>
          </TeContainerSlim>
          {renderSearch()}
        </TeContainerSlim>
      </TeContainerSlim>
    ),
    [renderSearch, t]
  );

  const renderIdentities = useCallback(() => {
    if (!isSearchActive || isLoading) return null;
    return identities.map((identity) => (
      <TopconIdUser key={identity.id} identity={identity} onSupport={handleSupport} />
    ));
  }, [handleSupport, identities, isLoading, isSearchActive]);

  const renderRecentIdentities = useCallback(() => {
    if (isLoading || recentIdentities.length === 0 || (identities.length > 0 && !isLoading))
      return null;
    return (
      <>
        <TeDivider
          text={t('mostRecentlyUsed')}
          textPlacement="center"
          dividerColor="border.primary"
          textColor="text.primary"
        />
        {recentIdentities.map((identity) => (
          <TopconIdUser key={identity.id} identity={identity} onSupport={handleSupport} />
        ))}
      </>
    );
  }, [handleSupport, identities.length, isLoading, recentIdentities, t]);

  const getPlaceholderText = useCallback(() => {
    if (!isSearchActive) return t('identitiesWillAppearAfterSearch');
    else if (isLoading) return t('loadingIdentities');
    else return t('noMatchingIdentities');
  }, [isLoading, isSearchActive, t]);

  const getPlaceholderLoading = useCallback(() => {
    if (!isSearchActive) return false;
    else return isLoading;
  }, [isLoading, isSearchActive]);

  const renderPlaceholder = useCallback(() => {
    if (identities.length > 0 && !isLoading) return null;
    return <TePlaceholderContainer text={getPlaceholderText()} loader={getPlaceholderLoading()} />;
  }, [getPlaceholderLoading, getPlaceholderText, isLoading, identities.length]);

  return (
    <TeContainer grow column overflow="hidden" alignItems="center">
      <PageWrapper>
        <TeContainerSlim
          column
          backgroundColor="white"
          grow
          borderColor="border.secondary"
          padding="xs"
          overflow="hidden"
          borderRadius="m"
        >
          {renderHeader()}
          <TeContainer
            backgroundColor="background.secondary"
            borderColor="border.secondary"
            borderRadius="m"
            gap="s"
            column
            fill
            overflow="hidden"
          >
            <TeContainerSlim fill overflow="auto" column gap="xs">
              {renderPlaceholder()}
              {renderIdentities()}
              {renderRecentIdentities()}
            </TeContainerSlim>
            {totalPages > 1 && (
              <TePagination
                pages={totalPages}
                page={selectedPage}
                techange={(e) => setSelectedPage(e.page)}
              />
            )}
          </TeContainer>
        </TeContainerSlim>
      </PageWrapper>
      <TeModal
        open={modalOpen}
        modalTitle={t('supportTokenFormHeader')}
        closeMode="overlay"
        teclose={() => setModalOpen(false)}
        closeable={true}
        width={modalStep === 'result' ? 's' : undefined}
      >
        <TeContainer column gap="s" padding="s">
          <TeSteps>
            <TeStep
              text={t('supportTokenStepForm')}
              icon={modalStep === 'result' ? 'check_circle_outline' : 'circle'}
              activeByDefault
              active={modalStep === 'form'}
            />
            <TeStep
              text={t('supportTokenStepResult')}
              icon="circle"
              active={modalStep === 'result'}
            />
          </TeSteps>
          {modalStep === 'form' && (
            <>
              <TeText>{t('supportTokenFormDescription')}</TeText>
              <TeContainer column gap="xs">
                <TeText weight="medium">{t('supportTokenReasonLabel')}</TeText>
                <TeTextarea
                  value={reason}
                  techange={(e) => setReason(e.target.value)}
                  placeholder={t('supportTokenReasonPlaceholder')}
                  maxLength={500}
                  rows={3}
                />
              </TeContainer>
              <TeContainer column gap="xs">
                <TeText weight="medium">{t('supportTokenTicketLabel')}</TeText>
                <TeInputText
                  value={ticket}
                  techange={(e) => setTicket(e.target.value)}
                  placeholder={t('supportTokenTicketPlaceholder')}
                  maxLength={100}
                />
              </TeContainer>
            </>
          )}
          {modalStep === 'result' && (
            <>
              <TeText>{t('supportTokenDescription')}</TeText>
              <TeContainer
                backgroundColor="background.secondary"
                borderColor="border.secondary"
                borderRadius="m"
                padding="s"
              >
                <TeText weight="medium" style={{ wordBreak: 'break-all' }}>
                  {supportToken}
                </TeText>
              </TeContainer>
            </>
          )}
        </TeContainer>
        {modalStep === 'form' && (
          <>
            <TeButton
              slot="tertiary"
              text={t('cancel')}
              variant="neutral"
              size="s"
              teclick={() => setModalOpen(false)}
            />
            <TeButton
              slot="primary"
              text={isGenerating ? t('generatingToken') : t('generateToken')}
              variant="primary"
              size="s"
              teclick={handleGenerateToken}
              disabled={!reason.trim() || !ticket.trim() || isGenerating}
            />
          </>
        )}
        {modalStep === 'result' && (
          <>
            <TeButton
              slot="tertiary"
              text={t('backToForm')}
              variant="neutral"
              size="s"
              teclick={() => setModalStep('form')}
            />
            <TeButton
              slot="primary"
              text={t('copyToken')}
              icon="content_copy"
              variant="primary"
              size="s"
              teclick={handleCopyToken}
            />
          </>
        )}
      </TeModal>
    </TeContainer>
  );
}

export default TopconId;
